import { usePathname, useSearchParams } from "next/navigation";
import { FC, useEffect } from "react";
import { useAppSelector } from "@/redux/hooks";
import signedInStatusSelector from "@/redux/slice/auth/selectors/signedInState";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import getPatientDetails from "@/redux/slice/auth/utils/getPatientDetails";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import { eventTracking } from "@/libs/clevertapTracking";
import { DeviceInfo } from "@/interface/deviceInfo";
import { LOGIN_POPUP_URL_PARAM, LOGIN_POPUP_URL_STATE } from "@/libs/constants/auth";

interface WebLaunchSourceProps {
  ua: DeviceInfo;
}

const WebLaunchSource: FC<WebLaunchSourceProps> = (props) => {
  const { ua } = props;
  const routerPathName = usePathname();
  const searchParams = useSearchParams();
  const isSignedIn = useAppSelector(signedInStatusSelector);
  const currentPatient = useAppSelector(currentPatientSelector);
  const patientDetails = getPatientDetails(currentPatient);

  useEffect(() => {
    const { utm_source, utm_medium, utm_campaign } = Object.fromEntries(
      ["utm_source", "utm_medium", "utm_campaign"].map((param) => [param, searchParams.get(param)])
    );
    const popupState = searchParams.get(LOGIN_POPUP_URL_PARAM);

    if (utm_source || utm_medium || utm_campaign) {
      Object.entries({ utm_source, utm_medium, utm_campaign }).forEach(([key, value]) => {
        if (value) {
          sessionStorage.setItem(key, value);
        }
      });
    }

    const currentReferrer = document?.referrer;

    if (!sessionStorage.getItem("referrer")) {
      sessionStorage.setItem("referrer", currentReferrer || "No referrer");
    }

    if (popupState === LOGIN_POPUP_URL_STATE) {
      return;
    }
  }, [searchParams]);

  useEffect(() => {
    if (isSignedIn !== SignedinState.UNRESOLVED) {
      const webLaunchEventData = {
        UTM_Source: searchParams.get("utm_source") || sessionStorage.getItem("utm_source") || "NA",
        UTM_Medium: searchParams.get("utm_medium") || sessionStorage.getItem("utm_medium") || "NA",
        UTM_Campaign: searchParams.get("utm_campaign") || sessionStorage.getItem("utm_campaign") || "NA",
        "Patient name": patientDetails?.patientName,
        "Patient UHID": patientDetails?.patientUhid,
        "Patient gender": patientDetails?.patientGender,
        "Patient age": patientDetails?.patientAge,
        "CT Source": "Web",
        "Mobile Number": patientDetails?.mobileNumber,
        Relation: currentPatient?.relation,
        "Customer ID": patientDetails?.patientId,
        URL: `${process.env.NEXT_PUBLIC_BASE_URL}${routerPathName.split("?")[0]}`,
        platform: ua?.isMobile ? "m-Web" : "Web",
        referrer: sessionStorage.getItem("referrer")?.trim() || "No referrer",
      };
      eventTracking("Web Launch Source CT", webLaunchEventData);
    }
  }, [isSignedIn]);

  return null;
};

export default WebLaunchSource;
